import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { X, Cookie, ChevronDown, ChevronUp } from "lucide-react";

const COOKIE_KEY = "ia_sanitaria_cookies";

export function CookieConsent() {
  const [visible, setVisible] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [preferences, setPreferences] = useState({
    necesarias: true,
    analiticas: false,
    marketing: false,
  });

  useEffect(() => {
    const saved = localStorage.getItem(COOKIE_KEY);
    if (!saved) {
      const timer = setTimeout(() => setVisible(true), 1000);
      return () => clearTimeout(timer);
    }
  }, []);

  const savePreferences = (prefs) => {
    localStorage.setItem(COOKIE_KEY, JSON.stringify(prefs));
    setVisible(false);
  };

  const acceptAll = () => {
    savePreferences({ necesarias: true, analiticas: true, marketing: true });
  };

  const acceptSelected = () => {
    savePreferences(preferences);
  };

  const rejectOptional = () => {
    savePreferences({ necesarias: true, analiticas: false, marketing: false });
  };

  if (!visible) return null;

  return (
    <div
      className="fixed bottom-0 left-0 right-0 z-[100] p-4 sm:p-6"
      data-testid="cookie-banner"
    >
      <div className="max-w-3xl mx-auto rounded-2xl border border-gray-700 bg-[#101421]/95 backdrop-blur-xl shadow-2xl shadow-black/40">
        <div className="p-5 sm:p-6">
          {/* Header */}
          <div className="flex items-start justify-between gap-4 mb-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-blue-500/15 border border-blue-500/25 flex items-center justify-center flex-shrink-0">
                <Cookie className="w-5 h-5 text-blue-400" />
              </div>
              <div>
                <h3 className="text-white font-bold text-base">Uso de cookies</h3>
                <p className="text-gray-500 text-xs">RGPD y LOPDGDD</p>
              </div>
            </div>
            <button
              onClick={rejectOptional}
              className="text-gray-500 hover:text-gray-300 transition-colors p-1"
              data-testid="cookie-close"
              aria-label="Cerrar"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Description */}
          <p className="text-gray-400 text-sm leading-relaxed mb-4">
            Utilizamos cookies propias y de terceros. Las necesarias permiten el funcionamiento del sitio. Puedes aceptar todas, configurarlas o rechazar las opcionales.
          </p>

          {/* Details toggle */}
          <button
            onClick={() => setShowDetails(!showDetails)}
            className="flex items-center gap-2 text-blue-400 text-sm font-medium mb-4 hover:text-blue-300 transition-colors"
            data-testid="cookie-toggle-details"
          >
            {showDetails ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            {showDetails ? "Ocultar opciones" : "Configurar cookies"}
          </button>

          {/* Cookie Options */}
          {showDetails && (
            <div className="space-y-3 mb-5 p-4 rounded-xl bg-gray-800/40 border border-gray-700/50">
              {/* Necessary - always on */}
              <label className="flex items-center justify-between">
                <div>
                  <span className="text-white text-sm font-medium">Necesarias</span>
                  <p className="text-gray-500 text-xs mt-0.5">Imprescindibles para el funcionamiento del sitio</p>
                </div>
                <div className="w-10 h-6 bg-blue-500 rounded-full flex items-center justify-end px-1 cursor-not-allowed opacity-70">
                  <div className="w-4 h-4 bg-white rounded-full"></div>
                </div>
              </label>

              {/* Analytics */}
              <label className="flex items-center justify-between cursor-pointer" data-testid="cookie-option-analiticas">
                <div>
                  <span className="text-white text-sm font-medium">Analíticas</span>
                  <p className="text-gray-500 text-xs mt-0.5">Nos ayudan a entender cómo se usa el sitio</p>
                </div>
                <button
                  type="button"
                  onClick={() => setPreferences(p => ({ ...p, analiticas: !p.analiticas }))}
                  className={`w-10 h-6 rounded-full flex items-center px-1 transition-colors ${preferences.analiticas ? "bg-blue-500 justify-end" : "bg-gray-600 justify-start"}`}
                >
                  <div className="w-4 h-4 bg-white rounded-full transition-all"></div>
                </button>
              </label>

              {/* Marketing */}
              <label className="flex items-center justify-between cursor-pointer" data-testid="cookie-option-marketing">
                <div>
                  <span className="text-white text-sm font-medium">Marketing</span>
                  <p className="text-gray-500 text-xs mt-0.5">Permiten mostrar contenido relevante</p>
                </div>
                <button
                  type="button"
                  onClick={() => setPreferences(p => ({ ...p, marketing: !p.marketing }))}
                  className={`w-10 h-6 rounded-full flex items-center px-1 transition-colors ${preferences.marketing ? "bg-blue-500 justify-end" : "bg-gray-600 justify-start"}`}
                >
                  <div className="w-4 h-4 bg-white rounded-full transition-all"></div>
                </button>
              </label>
            </div>
          )}

          {/* Buttons */}
          <div className="flex flex-col sm:flex-row gap-2">
            <Button
              onClick={acceptAll}
              className="btn-primary flex-1 text-sm py-2.5"
              data-testid="cookie-accept-all"
            >
              Aceptar todas
            </Button>
            {showDetails && (
              <Button
                onClick={acceptSelected}
                variant="outline"
                className="btn-secondary flex-1 text-sm py-2.5"
                data-testid="cookie-accept-selected"
              >
                Guardar selección
              </Button>
            )}
            <Button
              onClick={rejectOptional}
              variant="ghost"
              className="flex-1 text-sm py-2.5 text-gray-400 hover:text-white hover:bg-gray-800"
              data-testid="cookie-reject"
            >
              Solo necesarias
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
